import { useState, Fragment } from 'react';
import { Loader2, Search, ChevronDown, ChevronRight, AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const COL_COUNT = 13; // number of columns in the mismatch table

function fmtGBP(v) {
  if (v == null) return '-';
  return `\u00a3${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtRate(v) {
  if (v == null) return '-';
  return `\u00a3${Number(v).toFixed(0)}`;
}

function fmtPct(v, signed = true) {
  if (v == null) return '-';
  const sign = signed && v > 0 ? '+' : '';
  return `${sign}${Number(v).toFixed(1)}%`;
}

function BookingDetailTable({ bookings, label }) {
  if (!bookings?.length) return null;
  return (
    <div className="mt-2">
      <p className="text-xs font-medium text-muted-foreground mb-1">{label} ({bookings.length})</p>
      <div className="overflow-x-auto border rounded">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-left px-2 py-1 font-medium">Booking ID</th>
              <th className="text-left px-2 py-1 font-medium">Guest</th>
              <th className="text-left px-2 py-1 font-medium">Check-in</th>
              <th className="text-left px-2 py-1 font-medium">Check-out</th>
              <th className="text-right px-2 py-1 font-medium">Nights</th>
              <th className="text-right px-2 py-1 font-medium">Rev (GBP)</th>
              <th className="text-left px-2 py-1 font-medium">Status</th>
              <th className="text-left px-2 py-1 font-medium">Channel</th>
              <th className="text-left px-2 py-1 font-medium">property_id on booking</th>
              <th className="text-left px-2 py-1 font-medium">Synced</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-2 py-1 font-mono text-[10px]">{b.booking_id || '-'}</td>
                <td className="px-2 py-1">{b.guest_name || '-'}</td>
                <td className="px-2 py-1 tabular-nums">{b.check_in}</td>
                <td className="px-2 py-1 tabular-nums">{b.check_out}</td>
                <td className="px-2 py-1 text-right tabular-nums">{b.nights_in_month}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmtGBP(b.revenue_gbp)}</td>
                <td className="px-2 py-1">
                  {b.excluded ? (
                    <Badge variant="outline" className="text-[10px] text-orange-600">{b.exclusion_reason}</Badge>
                  ) : (
                    <span className="text-green-700 dark:text-green-400">{b.status}</span>
                  )}
                </td>
                <td className="px-2 py-1">{b.channel || '-'}</td>
                <td className="px-2 py-1 font-mono text-[10px]">{b.property_id_on_booking}</td>
                <td className="px-2 py-1 text-muted-foreground">{b.synced_at ? b.synced_at.slice(0, 16) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function OccupancyAuditPanel() {
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const runAudit = async () => {
    setLoading(true);
    setResults(null);
    setExpanded(new Set());
    try {
      const { data } = await diagnosticsAPI.occupancyAudit({ year, month });
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (pid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(pid) ? next.delete(pid) : next.add(pid);
      return next;
    });
  };

  const allProps = results?.properties ?? [];
  const mismatchedProps = allProps.filter(
    (p) => p.has_mismatch || p.bookings_found_by_name_only > 0
  );

  const mismatchCount = results?.properties_with_mismatch ?? 0;
  const totalMissingNights = results?.total_missing_nights_across_portfolio ?? 0;
  const orphanedIds = results?.orphaned_property_ids ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Occupancy &amp; Revenue Audit (Identifier Mismatch Detection)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={runAudit} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run Audit
          </Button>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* UK tab finding */}
            {results.uk_tab_april_finding && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5 mb-1">
                  <Info className="h-4 w-4 text-blue-500" />
                  UK Tab (April-onwards) Finding
                </p>
                <p className="text-xs text-muted-foreground">{results.uk_tab_april_finding}</p>
              </div>
            )}

            {/* Summary cards - row 1: occupancy */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Properties audited</p>
                <p className="text-xl font-bold tabular-nums">{results.total_properties}</p>
              </div>
              <div className={`rounded-lg border p-3 ${mismatchCount > 0 ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                <p className="text-xs text-muted-foreground">With mismatch</p>
                <p className={`text-xl font-bold tabular-nums ${mismatchCount > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{mismatchCount}</p>
              </div>
              <div className={`rounded-lg border p-3 ${totalMissingNights > 0 ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                <p className="text-xs text-muted-foreground">Missing nights (total)</p>
                <p className={`text-xl font-bold tabular-nums ${totalMissingNights > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{totalMissingNights}</p>
              </div>
              <div className={`rounded-lg border p-3 ${orphanedIds.length > 0 ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                <p className="text-xs text-muted-foreground">Orphaned property_ids</p>
                <p className={`text-xl font-bold tabular-nums ${orphanedIds.length > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{orphanedIds.length}</p>
              </div>
            </div>

            {/* Summary cards - row 2: revenue impact */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Revenue visible (GBP)</p>
                <p className="text-xl font-bold tabular-nums">{fmtGBP(results.total_revenue_visible_gbp)}</p>
              </div>
              <div className={`rounded-lg border p-3 ${results.total_revenue_invisible_gbp > 0 ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                <p className="text-xs text-muted-foreground">Revenue invisible (GBP)</p>
                <p className={`text-xl font-bold tabular-nums ${results.total_revenue_invisible_gbp > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {fmtGBP(results.total_revenue_invisible_gbp)}
                </p>
                {results.total_revenue_invisible_pct > 0 && (
                  <p className="text-xs text-red-500 mt-0.5">{fmtPct(results.total_revenue_invisible_pct, false)} of total</p>
                )}
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Portfolio rate (visible)</p>
                <p className="text-xl font-bold tabular-nums">{fmtRate(results.portfolio_rate_visible)}/night</p>
              </div>
              <div className={`rounded-lg border p-3 ${results.portfolio_rate_distortion_pct != null && results.portfolio_rate_distortion_pct !== 0 ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950' : ''}`}>
                <p className="text-xs text-muted-foreground">True rate / distortion</p>
                <p className="text-xl font-bold tabular-nums">{fmtRate(results.portfolio_rate_true)}/night</p>
                {results.portfolio_rate_distortion_pct != null && results.portfolio_rate_distortion_pct !== 0 && (
                  <p className={`text-xs mt-0.5 ${results.portfolio_rate_distortion_pct > 0 ? 'text-amber-600' : 'text-blue-600'}`}>
                    {fmtPct(results.portfolio_rate_distortion_pct)} distortion
                  </p>
                )}
              </div>
            </div>

            {/* Mismatch table */}
            {mismatchedProps.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Properties with booking mismatches</p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="w-8 px-2 py-2" />
                        <th className="text-left px-3 py-2 font-medium">Property</th>
                        <th className="text-left px-3 py-2 font-medium">Expected ID</th>
                        <th className="text-right px-3 py-2 font-medium">By ID</th>
                        <th className="text-right px-3 py-2 font-medium">Nights vis.</th>
                        <th className="text-right px-3 py-2 font-medium">By name</th>
                        <th className="text-right px-3 py-2 font-medium">Nights inv.</th>
                        <th className="text-right px-3 py-2 font-medium">Rev vis.</th>
                        <th className="text-right px-3 py-2 font-medium">Rev inv.</th>
                        <th className="text-right px-3 py-2 font-medium">Rate vis.</th>
                        <th className="text-right px-3 py-2 font-medium">Rate true</th>
                        <th className="text-right px-3 py-2 font-medium">Distortion</th>
                        <th className="text-left px-3 py-2 font-medium">Mismatched IDs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mismatchedProps.map((p) => {
                        const isExpanded = expanded.has(p.property_id);
                        return (
                          <Fragment key={p.property_id}>
                            <tr className="border-b">
                              <td className="px-2 py-2">
                                <button onClick={() => toggleRow(p.property_id)} className="text-muted-foreground hover:text-foreground">
                                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </button>
                              </td>
                              <td className="px-3 py-2">
                                <div className="font-medium">{p.name}</div>
                                <div className="text-xs text-muted-foreground">{p.platform} / {p.region}</div>
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">{p.property_id}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{p.bookings_found_by_property_id}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{p.nights_visible}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                <span className={p.bookings_found_by_name_only > 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                                  {p.bookings_found_by_name_only}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                <span className={p.nights_invisible > 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                                  {p.nights_invisible}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtGBP(p.revenue_visible_gbp)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                <span className={p.revenue_invisible_gbp > 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                                  {fmtGBP(p.revenue_invisible_gbp)}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtRate(p.rate_visible)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtRate(p.rate_true)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {p.rate_distortion_pct != null ? (
                                  <span className={p.rate_distortion_pct > 0 ? 'text-amber-600' : p.rate_distortion_pct < 0 ? 'text-blue-600' : ''}>
                                    {fmtPct(p.rate_distortion_pct)}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="px-3 py-2 font-mono text-xs max-w-[200px]">
                                {p.mismatched_property_ids?.join(', ') || '-'}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="border-b bg-muted/20">
                                <td colSpan={COL_COUNT} className="px-4 py-3">
                                  <BookingDetailTable
                                    bookings={p.bookings_by_property_id}
                                    label="Bookings matched by property_id (visible to portfolio)"
                                  />
                                  <BookingDetailTable
                                    bookings={p.bookings_by_name_only_mismatch}
                                    label="Bookings matched by name only (INVISIBLE to portfolio)"
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {mismatchedProps.length === 0 && (
              <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                No identifier mismatches detected for {results.month_key}.
              </p>
            )}

            {/* All properties revenue summary */}
            {allProps.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">All properties: revenue summary</p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Property</th>
                        <th className="text-left px-3 py-2 font-medium">Platform</th>
                        <th className="text-right px-3 py-2 font-medium">Nights vis.</th>
                        <th className="text-right px-3 py-2 font-medium">Nights inv.</th>
                        <th className="text-right px-3 py-2 font-medium">Rev vis.</th>
                        <th className="text-right px-3 py-2 font-medium">Rev inv.</th>
                        <th className="text-right px-3 py-2 font-medium">Rev inv. %</th>
                        <th className="text-right px-3 py-2 font-medium">Rate vis.</th>
                        <th className="text-right px-3 py-2 font-medium">Rate true</th>
                        <th className="text-right px-3 py-2 font-medium">Distortion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allProps.map((p) => (
                        <tr key={p.property_id} className={`border-b last:border-0 ${p.has_mismatch ? 'bg-red-50/50 dark:bg-red-950/30' : ''}`}>
                          <td className="px-3 py-2 font-medium">{p.name}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{p.platform} / {p.region}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{p.nights_visible}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <span className={p.nights_invisible > 0 ? 'text-red-600 dark:text-red-400' : ''}>{p.nights_invisible}</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtGBP(p.revenue_visible_gbp)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <span className={p.revenue_invisible_gbp > 0 ? 'text-red-600 dark:text-red-400' : ''}>{fmtGBP(p.revenue_invisible_gbp)}</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <span className={p.revenue_invisible_pct > 0 ? 'text-red-600 dark:text-red-400' : ''}>{fmtPct(p.revenue_invisible_pct, false)}</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtRate(p.rate_visible)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtRate(p.rate_true)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {p.rate_distortion_pct != null ? (
                              <span className={p.rate_distortion_pct > 0 ? 'text-amber-600' : p.rate_distortion_pct < 0 ? 'text-blue-600' : ''}>
                                {fmtPct(p.rate_distortion_pct)}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Orphaned property_ids */}
            {orphanedIds.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Orphaned property_ids (in bookings, not in any portfolio property)</p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">property_id</th>
                        <th className="text-left px-3 py-2 font-medium">Platform</th>
                        <th className="text-right px-3 py-2 font-medium">Bookings</th>
                        <th className="text-left px-3 py-2 font-medium">Sample property_name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphanedIds.map((o, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{o.property_id}</td>
                          <td className="px-3 py-2">{o.platform}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{o.booking_count_in_month}</td>
                          <td className="px-3 py-2">{o.sample_property_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
